import React from "react";

import {convertToCountryLatLngLiteral} from "@pg-mono/open-street-map";
import {rpAppLink} from "@pg-mono/rp-routes";
import {pluralize} from "@pg-mono/string-utils";

import {rpCommonUrl} from "../../app/read_rp_environment_variables";
import {generateFallbackDescription} from "../../offer/detail/schema/description_util";
import {getOfferTypeName, getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {OfferType} from "../../offer/types/OfferType";
import {Country} from "../../region/types/Country";

interface IProps {
    property: {
        area: number;
        for_sale: boolean;
        id: number;
        is_active?: boolean;
        number: string;
        offer: {
            geo_point: {
                coordinates: [number, number];
            };
            id: number;
            main_image: {
                m_img_1500: string;
            };
            name: string;
            slug: string;
            street_name: string;
            street_number: string;
            region: {
                country: Country;
                full_name: string;
                name_declension_where: string;
            };
            stats: {
                distance_from_region: {
                    center: boolean;
                    region: string;
                    distance: number;
                };
            } | null;
            type: number;
            vendor: {
                name: string;
                slug: string;
            };
        };
        price: number | null;
        rooms: number;
        floor: number;
    };
}

export const SchemaPropertyDetail: React.FunctionComponent<IProps> = (props) => {
    const {property} = props;
    const isNotCommercial = property.offer.type === OfferType.HOUSE || property.offer.type === OfferType.FLAT;
    const url = rpAppLink.property.detail.base({
        vendorSlug: property.offer.vendor.slug,
        offerSlug: property.offer.slug,
        offerId: property.offer.id,
        propertyId: property.id
    });
    const {lng, lat} = convertToCountryLatLngLiteral(property.offer.geo_point.coordinates, property.offer.region.country);
    let offerType: string;
    if (property.offer.type === OfferType.HOUSE) {
        offerType = "SingleFamilyResidence";
    } else if (property.offer.type === OfferType.FLAT) {
        offerType = "Apartment";
    } else {
        offerType = "Place";
    }
    const floorSizeValue = {
        value: property.area,
        unitCode: "MTK"
    };
    const floorSize = isNotCommercial ? floorSizeValue : undefined;
    const address = `${property.offer.street_name} ${property.offer.street_number ? property.offer.street_number : ""}`;
    const roomPluralize = pluralize(["pokój", "pokoje", "pokoi"]);
    const name = `${property.offer.name} - ${getOfferTypeName(property.offer.type)} ${property.rooms} ${roomPluralize(property.rooms)}, ${property.area} m², ${
        property.floor === 0 ? "Parter" : property.floor
    } `;
    const description = `${getOfferTypeNameCapital(property.offer.type)} na sprzedaż ${property.rooms} ${roomPluralize(property.rooms)} o powierzchni ${
        property.area
    } m²`;

    const isActivePropertyWithPrice = property.is_active && property.for_sale && !!property.price;

    const productValue = {
        "@context": "https://schema.org/",
        "@type": "Product",
        name: name,
        image: property.offer.main_image.m_img_1500,
        description: description ? description : generateFallbackDescription(property.offer),
        brand: {
            name: property.offer.vendor.name
        },
        offers: {
            "@type": "Offer",
            url: `${rpCommonUrl}${url}`,
            priceCurrency: "PLN",
            price: property.price
        }
    };

    const jsonOffer = {
        "@context": "http://schema.org/",
        "@type": offerType,
        name: property.number,
        numberOfRooms: isNotCommercial ? property.rooms : undefined,
        floorSize,
        url: url,
        containedIn: {
            name: property.offer.name
        },
        address: {
            streetAddress: address,
            addressLocality: property.offer.region.full_name,
            addressCountry: {
                name: "PL"
            }
        },
        geo: {
            "@type": "GeoCoordinates",
            latitude: lat,
            longitude: lng
        },
        photo: property.offer.main_image.m_img_1500,
        ...(isActivePropertyWithPrice && {productValue: {...productValue}})
    };

    return (
        <span>
            <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(jsonOffer)}} />
        </span>
    );
};
